import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { 
  FileWarning, 
  Plus, 
  Loader2, 
  AlertCircle, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_DOC_TYPES = ['Physical Exam', 'Liability Waiver', 'Birth Certificate', 'Insurance Card', 'Medical Clearance', 'Other'];

function getExpiryStatus(expiryDate) {
  if (!expiryDate) return { label: 'No Expiry', color: 'bg-slate-600', urgency: 'none' };
  const now = new Date();
  const expiry = new Date(expiryDate);
  const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) return { label: 'Expired', color: 'bg-red-600', urgency: 'expired' };
  if (daysUntil <= 7) return { label: `${daysUntil}d left`, color: 'bg-red-600', urgency: 'critical' };
  if (daysUntil <= 30) return { label: `${daysUntil}d left`, color: 'bg-amber-600', urgency: 'warning' };
  if (daysUntil <= 90) return { label: `${daysUntil}d left`, color: 'bg-yellow-600', urgency: 'notice' };
  return { label: 'Valid', color: 'bg-green-600', urgency: 'ok' };
}

export default function DocumentAlerts({ orgId, playerId, familyView = false }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [docTypes, setDocTypes] = useState(DEFAULT_DOC_TYPES);

  const [form, setForm] = useState({
    player_id: playerId || '',
    doc_type: 'Physical Exam',
    doc_name: '',
    expiry_date: '',
    notes: '',
  });

  const fetchDocuments = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (playerId) params.append('player_id', playerId);

      const [docRes, orgsRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents/org/${orgId}?${params}`),
        axios.get(`${API_URL}/api/organizations`),
      ]);
      setDocuments(docRes.data || []);
      const org = (orgsRes.data || []).find(o => o.id === orgId);
      if (org?.settings?.document_types?.length) setDocTypes(org.settings.document_types);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, playerId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/documents/org/${orgId}`, {
        ...form,
        expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
      });
      
      setShowAdd(false);
      setForm({ player_id: playerId || '', doc_type: 'Physical Exam', doc_name: '', expiry_date: '', notes: '' });
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const expiring = documents.filter(d => {
    const status = getExpiryStatus(d.expiry_date);
    return ['expired', 'critical', 'warning'].includes(status.urgency);
  });

  return (
    <div className="space-y-4">
      {/* Alert Banner for expiring docs */}
      {expiring.length > 0 && (
        <div className={`p-3 rounded-lg border flex items-center gap-3 ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
          <Bell className={`h-5 w-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
          <div>
            <p className={`font-medium text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
              {expiring.length} document{expiring.length !== 1 ? 's' : ''} need{expiring.length === 1 ? 's' : ''} attention
            </p>
            <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-500'}`}>
              Review expiring or expired documents below
            </p>
          </div>
        </div>
      )}

      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <FileWarning className="h-5 w-5 text-amber-500" />
            Documents
          </CardTitle>
          <Button 
            size="sm" 
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => setShowAdd(true)}
            data-testid="add-document-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <FileWarning className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No documents tracked</p>
              <p className="text-sm">Add player documents to receive expiry alerts</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => {
                const status = getExpiryStatus(doc.expiry_date);
                const StatusIcon = status.urgency === 'ok' ? CheckCircle2 
                  : status.urgency === 'none' ? Clock 
                  : AlertTriangle;

                return (
                  <div 
                    key={doc.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}
                    data-testid={`doc-${doc.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <StatusIcon className={`h-4 w-4 ${
                        status.urgency === 'ok' ? 'text-green-500' 
                        : status.urgency === 'none' ? 'text-slate-400'
                        : 'text-amber-500'
                      }`} />
                      <div>
                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {doc.doc_name || doc.doc_type}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {doc.player_name || 'Player'} · {doc.doc_type}
                          {doc.expiry_date && ` · Expires ${new Date(doc.expiry_date).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <Badge className={status.color}>{status.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Document Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Add Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Document Type *</Label>
                <select
                  value={form.doc_type}
                  onChange={(e) => setForm({...form, doc_type: e.target.value})}
                  className={`w-full h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="doc-type-select"
                >
                  {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Expiry Date</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm({...form, expiry_date: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="doc-expiry-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Document Name</Label>
              <Input
                value={form.doc_name}
                onChange={(e) => setForm({...form, doc_name: e.target.value})}
                placeholder="e.g. 2025 Physical"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="doc-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Optional notes"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="doc-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="doc-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Document'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
